import { useEffect } from 'react'
import { useAuthOptional } from '../contexts/auth-context'
import { isSessionsNavVisibleFor } from '../config/nav-config'
import { resetCoalescedReads } from '../services/appsync-client'
import {
  clearAllLocalCache,
  purgeLocalCacheExceptUser,
  sweepExpiredLocalCache,
} from '../services/local-cache'
import {
  prefetchSessionsList,
  resetSessionsPrefetch,
} from '../features/sessions/data/sessions-list-loader'

/**
 * Owns the lifecycle of the per-user local screen cache (`services/local-cache`).
 * Always mounted, auth-agnostic (reads auth, never changes it) — same wiring as
 * `ActiveSessionBanner`'s sign-out teardown.
 *
 *  - Signed out (auth resolved, no user): wipe every cached snapshot. This is
 *    in addition to the explicit step in `signOutUser`, so session expiry and
 *    cold starts with a dead session are covered too (shared-phone safety).
 *  - Signed in as `userId`: purge every entry that belongs to anyone else
 *    (account switch without a signed-out render) or to an older schema.
 *  - App start / resume: sweep entries past the TTL or from an old schema.
 *  - Signed in / app resumed: prefetch the Sessions list so the screen opens
 *    onto data that is already in flight.
 */
export function LocalCacheLifecycle() {
  const auth = useAuthOptional()
  const user = auth?.user ?? null
  const isLoading = auth?.isLoading ?? true
  const userId = user?.userId ?? null
  const canSeeSessions = user ? isSessionsNavVisibleFor(user) : false

  useEffect(() => {
    if (isLoading || userId) return
    // Session expiry / dead session on a cold start never goes through
    // `signOutUser`: drop the coalesced `me` / inbox reads here too so the next
    // account (or a call started during teardown) can't be served the old user.
    try {
      resetCoalescedReads()
    } catch {
      /* best-effort */
    }
    resetSessionsPrefetch()
    void clearAllLocalCache().catch(() => {})
  }, [isLoading, userId])

  // Nothing lingers on disk past its TTL between app opens.
  useEffect(() => {
    void sweepExpiredLocalCache().catch(() => {})
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sweepExpiredLocalCache().catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  useEffect(() => {
    if (!userId) return
    // Order matters: the purge bumps the cache epoch synchronously, so a
    // prefetch started after it is never dropped, while one started for the
    // previous user (before the purge) cannot write.
    void purgeLocalCacheExceptUser(userId).catch(() => {})
  }, [userId])

  useEffect(() => {
    if (!userId || !canSeeSessions) return
    prefetchSessionsList(userId)
    const onVisible = () => {
      if (document.visibilityState === 'visible') prefetchSessionsList(userId)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [userId, canSeeSessions])

  return null
}
